import { AcademicCapIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { Flexor, Spinner } from 'shared/src/components';
import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'shared/src/components/ui/new';
import { toast } from 'sonner';
import { Link } from 'wouter';
import { useAppSelector } from '../hooks';
import { dropClass, getScheduledClasses } from '../fetchers';
import { ManageScheduledClass } from './ModalManageScheduledClass';
import PageEmptyState from './PageEmptyState';
import { renderDataAttribute } from './WorkSchedules';

export type Document = {
  Id: string;
  Name: string;
  Url: string;
  UploadDate: string;
  UploadBy: string;
};

export type ClassSession = {
  ClassName: string;
  Location: string;
  Instructor: string;
  ContactPhone: string;
  StartDate: string;
  StartTime: string;
  EndTime: string;
  Address: string;
  Room: string;
  Capacity: number;
  SeatsFilled: number;
  Documents: Document[];
};

type ScheduledClassSummary = {
  Id: string;
  ClassName: string;
  ClassDate?: string;
  StartTime?: string;
  Address?: string;
  Venue?: string;
};

type CurrentPollWorkerUser = {
  EVUserId: string;
};

export default function ScheduledClasses() {
  const selectedElectionId = useAppSelector((state) => state.election.electionId);
  const currentUser = useAppSelector((state) => state.currentPollworkerUser as CurrentPollWorkerUser);
  const [scheduledClasses, setScheduledClasses] = useState<ScheduledClassSummary[]>([]);
  const [scheduledClassId, setScheduledClassId] = useState<string>('');
  const [showScheduleView, setShowScheduleView] = useState(false);
  const [showDropClassView, setShowDropClassView] = useState(false);
  const [loadingScheduledClasses, setLoadingScheduledClasses] = useState(false);

  useEffect(() => {
    if (!selectedElectionId) return;
    loadScheduledClasses();
  }, [selectedElectionId]);

  function loadScheduledClasses() {
    if (!selectedElectionId) return;

    setLoadingScheduledClasses(true);

    getScheduledClasses(selectedElectionId)
      .then((response) => {
        setScheduledClasses(response as ScheduledClassSummary[]);
      })
      .finally(() => {
        setLoadingScheduledClasses(false);
      });
  }

  async function dropClassSession(sessionId: string) {
    try {
      const response = await dropClass(currentUser.EVUserId, sessionId);

      if (!response) {
        toast.error('An error occurred.');
        return;
      }

      toast('You have successfully dropped this class session.');
      loadScheduledClasses();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'An error occurred.');
    } finally {
      setShowDropClassView(false);
    }
  }

  return (
    <div className={'flow-root mt-2'}>
      <div>
        <Dialog size="md" open={showDropClassView} onOpenChange={(open) => !open && setShowDropClassView(false)}>
          <DialogContent className="p-0 gap-0 overflow-hidden max-w-xl">
            <DialogHeader>
              <DialogTitle>Drop Class Session</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <p className="text-md text-gray-700">Are you sure you want to remove yourself from this training session?</p>
            </DialogBody>
            <DialogFooter>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowDropClassView(false);
                  setShowScheduleView(true);
                }}
              >
                No
              </Button>
              <Button size="sm" onClick={() => dropClassSession(scheduledClassId)}>
                Yes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <ManageScheduledClass
        setShowDropClassView={setShowDropClassView}
        setShowScheduleView={setShowScheduleView}
        showScheduleView={showScheduleView}
        scheduledClassId={scheduledClassId}
      />

      <div>
        <h1 className="text-base font-semibold leading-6 text-gray-900 mb-4">Scheduled Classes</h1>
        <div className="sm:flex-auto">
          <p className="mt-2 text-sm text-gray-700">
            This list contains the class sessions which you are attending. You may cancel your reservation and reschedule to attend a different session. Be sure to view the
            attached documents so that you can meet the requirements for the subject matter in which you are to be trained.
          </p>
        </div>
      </div>

      {loadingScheduledClasses ? (
        <div className="my-24 flex flex-col justify-center items-center">
          <Flexor className="space-x-3">
            <Spinner />
            <span>Loading scheduled classes...</span>
          </Flexor>
        </div>
      ) : (
        <>
          {scheduledClasses.length ? (
            <ul className="divide-y divide-gray-300">
              {scheduledClasses?.map((scheduledClass) => (
                <li key={scheduledClass.Id} className="flex items-center justify-between gap-x-6 py-5">
                  <div className="min-w-0">
                    <div className="flex items-start gap-x-3">
                      <p className="text-sm font-semibold leading-6 text-gray-900">{scheduledClass.ClassName || 'Not specified'}</p>
                    </div>
                    <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
                      <span role="note">
                        <time id="classdate" dateTime={scheduledClass.ClassDate}>
                          {scheduledClass.ClassDate || 'Not specified'}
                        </time>{' '}
                        <span id="at">at</span>{' '}
                        <time id="time" dateTime={scheduledClass.StartTime}>
                          {' '}
                          {scheduledClass.StartTime}
                        </time>{' '}
                        at{' '}
                        <span id="location">
                          {scheduledClass.Venue}, {renderDataAttribute(scheduledClass.Address)}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-none items-center gap-x-4">
                    <Button
                      size="sm"
                      type="button"
                      id={`manage-class-${scheduledClass.Id}`}
                      onClick={() => {
                        setShowScheduleView(true);
                        setScheduledClassId(scheduledClass.Id);
                      }}
                    >
                      Manage Class
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="my-10">
              <PageEmptyState
                icon={AcademicCapIcon}
                emptyMessageHeader="No scheduled classes"
                emptyMessageSubHeader="No classes have been scheduled. Sign up for classes on the Required Classes page."
              >
                <Link to="~/classes/required">
                  <Button size="sm" className="flex mx-auto mt-4">
                    <ArrowLeftIcon className="h-4 w-4" />
                    <span>Go to Required Classes</span>
                  </Button>
                </Link>
              </PageEmptyState>
            </div>
          )}
        </>
      )}
    </div>
  );
}
